import { getElementResizeInsetAdjustment } from "@skilitics/drag-drop";

import { useEventSubscribe } from "../../events/index.ts";
import { getNodeElement } from "../element-state/utils.ts";
import { useEditingCss } from "./EditingCssPropertiesContext.ts";
import { propertiesEventTypes, type ResizeNodeDimensionsEventOptions } from "./propertiesEventTypes.ts";

type Props = {
  nodeId: number;
};

/**
 * Subscribes to resize events from canvas resize handles and updates
 * the node's CSS width/height through the EditingCssPropertiesContext.
 * Also switches layout behavior to "fixed" for resized dimensions,
 * and adjusts inset properties when the offset origin requires it
 * (including rotation-pivot drift compensation for rotated elements).
 */
export const ResizeUpdateSubscriber = ({ nodeId }: Props) => {
  const { layoutSettings, onCssPropertiesChange, onLayoutSettingsChange } = useEditingCss();

  useEventSubscribe(
    propertiesEventTypes.RESIZE_NODE_DIMENSIONS,
    ({ nodeId: eventNodeId, handle, widthDelta, heightDelta, width, height }: ResizeNodeDimensionsEventOptions) => {
      if (eventNodeId !== nodeId) return;

      const cssUpdate: Record<string, string> = {};
      const layoutUpdate: Record<string, string> = {};

      if (width !== undefined) {
        cssUpdate.width = width as string;
        layoutUpdate.horizontalBehavior = "fixed";
      }

      if (height !== undefined) {
        cssUpdate.height = height as string;
        layoutUpdate.verticalBehavior = "fixed";
      }

      if (layoutSettings.offsetOrigin) {
        const nodeElement = getNodeElement(nodeId);
        if (nodeElement) {
          const insetAdjustment = getElementResizeInsetAdjustment({
            element: nodeElement,
            offsetOrigin: layoutSettings.offsetOrigin,
            handle,
            widthDelta: widthDelta ?? 0,
            heightDelta: heightDelta ?? 0,
          });
          if (insetAdjustment) Object.assign(cssUpdate, insetAdjustment);
        }
      }

      if (Object.keys(layoutUpdate).length) {
        onLayoutSettingsChange(layoutUpdate);
      }
      if (Object.keys(cssUpdate).length) {
        onCssPropertiesChange(cssUpdate);
      }
    },
  );

  return null;
};
